import React from "react";
import { ReplacedSymbolsOracleText } from "modules/common/components/ManaFontSymbols";
import { GameCard } from "modules/cards/model/card";
import { getCardPresentationAdapter } from "modules/common/gameRegistry";

export interface Props {
  card: GameCard;
}

/**
 * Ruling dates are date-only values parsed as UTC midnight, so the zone is
 * pinned the same way `toDateFormat` pins it — an unpinned zone would render
 * the previous day west of UTC. Spelled out here only because rulings want a
 * four-digit year; everything else about it follows the date-only rule.
 */
function formatRulingDate(date: string): string {
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  // eslint-disable-next-line no-restricted-syntax -- locale and zone both pinned; deterministic on server and client
  return value.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export default function CardRulings({ card }: Props) {
  const rulings = getCardPresentationAdapter(card.game).rulings(card);
  if (!rulings) {
    return <></>;
  }

  return (
    <section aria-labelledby="rulings-heading">
      <div className="divider my-4"></div>
      <header>
        <h3 className="mb-2 text-base font-medium" id="rulings-heading">
          Rules & Rulings
        </h3>
      </header>
      <div role="region" aria-labelledby="rulings-heading">
        {rulings.length ? (
          <div className="space-y-4 pb-4" role="list" aria-label="Card rulings">
            {rulings.map((ruling, idx: number) => (
              <article
                key={idx}
                className="flex flex-col space-y-1"
                role="listitem"
              >
                <time className="text-secondary text-sm" dateTime={ruling.date}>
                  {formatRulingDate(ruling.date)}
                </time>
                <ReplacedSymbolsOracleText
                  oracleText={ruling.text}
                  className="text-base-content ml-0.5 text-base"
                />
              </article>
            ))}
          </div>
        ) : (
          <div className="my-2">
            <p className="text-secondary text-sm italic">
              No rulings for this card
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
